import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ExternalLink, Globe, Lock, Megaphone, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  listProjects,
  createProject,
  deleteProject,
  updateProject,
  type Project,
} from '@/api/projects'
import { resendVerification } from '@/api/auth'
import { cn } from '@/lib/utils'
import { WEB_BASE_URL } from '@/lib/constants'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

// ── Create project form ────────────────────────────────────────────────────

const RESERVED_SLUGS = new Set([
  'app', 'www', 'api', 'admin', 'mail', 'support', 'help', 'blog', 'docs',
  'status', 'about', 'terms', 'privacy', 'login', 'signup', 'dashboard',
  'settings', 'billing', 'account', 'user', 'users', 'project', 'projects',
  'mock', 'mocks', 'endpoint', 'endpoints', 'scenario', 'scenarios',
  'response', 'responses', 'log', 'logs', 'webhook', 'webhooks',
])

const createSchema = z.object({
  slug: z
    .string()
    .min(3)
    .max(32)
    .regex(/^[a-z0-9-]+$/)
    .refine((s) => !RESERVED_SLUGS.has(s)),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  visibility: z.enum(['private', 'public']).default('private'),
})

type CreateFormValues = z.infer<typeof createSchema>

function CreateProjectDialog({ onCreated, disabled = false }: { onCreated: () => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setOpen(false)
      onCreated()
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateFormValues>({ resolver: zodResolver(createSchema) })

  const onSubmit = async (data: CreateFormValues) => {
    setServerError(null)
    try {
      await mutation.mutateAsync(data)
      reset()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('slug', { message: t('dashboard.createDialog.slugTaken') })
      } else {
        setServerError(err instanceof ApiError ? err.message : t('dashboard.createDialog.createFailed'))
      }
    }
  }

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (!v) {
      reset()
      setServerError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button disabled={disabled}>
          <Plus className="mr-2 h-4 w-4" />
          {t('dashboard.newProject')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dashboard.createDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.createDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {serverError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="slug">{t('dashboard.createDialog.slug')}</Label>
            <Input
              id="slug"
              placeholder="my-api"
              {...register('slug')}
            />
            <p className="text-xs text-muted-foreground">{t('dashboard.createDialog.slugHint')}</p>
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">{t('dashboard.createDialog.name')}</Label>
            <Input id="name" placeholder="My API" {...register('name')} />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('dashboard.createDialog.description')}</Label>
            <Textarea
              id="description"
              placeholder={t('dashboard.createDialog.descriptionPlaceholder')}
              rows={2}
              {...register('description')}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.creating') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Delete confirmation ────────────────────────────────────────────────────

function DeleteProjectButton({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: () => deleteProject(project.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      setOpen(false)
      toast.success(t('dashboard.deleteDialog.success'))
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('dashboard.deleteDialog.failed'))
    },
  })

  const handleOpenChange = (v: boolean) => {
    if (mutation.isPending) return
    setOpen(v)
    if (!v) setError(null)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('dashboard.deleteDialog.title')}</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{project.name}</span>（{project.slug}）{t('dashboard.deleteDialog.description')}{t('common.cannotUndo')}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Project card ───────────────────────────────────────────────────────────

function ProjectCard({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()

  const toggleMutation = useMutation({
    mutationFn: (visibility: 'private' | 'public') =>
      updateProject(project.id, { visibility }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(updated.visibility === 'public' ? t('dashboard.visibility.changedPublic') : t('dashboard.visibility.changedPrivate'))
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('dashboard.visibility.changeFailed'))
    },
  })

  const shareMutation = useMutation({
    mutationFn: (is_shared: boolean) => updateProject(project.id, { is_shared }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (updated.is_shared) {
        const publicSlug = user?.domain ? `${user.domain}--${updated.slug}` : updated.slug
        const shareUrl = `${WEB_BASE_URL}/shared/${publicSlug}`
        const intentText = t('dashboard.visibility.shareOnXText', { name: updated.name })
        const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(intentText)}&url=${encodeURIComponent(shareUrl)}`
        toast.success(t('dashboard.visibility.changedSharedOn'), {
          action: {
            label: t('dashboard.visibility.shareOnX'),
            onClick: () => window.open(intentUrl, '_blank', 'noopener,noreferrer'),
          },
        })
      } else {
        toast.success(t('dashboard.visibility.changedSharedOff'))
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) {
        toast.error(t('dashboard.visibility.shareBlockedByReports'))
      } else {
        toast.error(err instanceof ApiError ? err.message : t('dashboard.visibility.shareChangeFailed'))
      }
    },
  })

  const handleToggleVisibility = () => {
    const next = project.visibility === 'public' ? 'private' : 'public'
    toggleMutation.mutate(next)
  }

  const handleToggleShare = () => {
    if (!project.is_shared && !user?.display_name) {
      toast.error(t('dashboard.visibility.shareRequiresDisplayName'), {
        action: {
          label: t('dashboard.visibility.shareRequiresDisplayNameLink'),
          onClick: () => navigate('/account'),
        },
      })
      return
    }
    shareMutation.mutate(!project.is_shared)
  }

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2 space-y-2">
        <div className="min-w-0">
          <CardTitle className="text-base truncate">{project.name}</CardTitle>
          <CardDescription className="text-xs mt-0.5 font-mono truncate">
            {project.slug}
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {project.visibility === 'public' && (
            <button
              onClick={handleToggleShare}
              disabled={shareMutation.isPending}
              title={project.is_shared ? t('dashboard.visibility.shareOn') : t('dashboard.visibility.shareOff')}
              className={cn(
                'shrink-0 rounded-full transition-opacity',
                shareMutation.isPending ? 'opacity-50 cursor-wait' : 'hover:opacity-70 cursor-pointer',
              )}
            >
              <Badge variant={project.is_shared ? 'default' : 'outline'}>
                <Megaphone className="mr-1 h-3 w-3" />
                {t('dashboard.visibility.shareLabel')}
              </Badge>
            </button>
          )}
          <button
            onClick={handleToggleVisibility}
            disabled={toggleMutation.isPending}
            title={project.visibility === 'public' ? t('dashboard.visibility.makePrivate') : t('dashboard.visibility.makePublic')}
            className={cn(
              'shrink-0 rounded-full transition-opacity',
              toggleMutation.isPending ? 'opacity-50 cursor-wait' : 'hover:opacity-70 cursor-pointer',
            )}
          >
            <Badge variant={project.visibility === 'public' ? 'default' : 'secondary'}>
              {project.visibility === 'public' ? (
                <Globe className="mr-1 h-3 w-3" />
              ) : (
                <Lock className="mr-1 h-3 w-3" />
              )}
              {project.visibility === 'public' ? 'Public' : 'Private'}
            </Badge>
          </button>
        </div>
      </CardHeader>
      {project.description && (
        <CardContent className="py-0 pb-3">
          <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
        </CardContent>
      )}
      <CardFooter className="mt-auto pt-3 flex items-center justify-between">
        <Button asChild variant="outline" size="sm">
          <Link to={`/projects/${project.id}`}>
            <ExternalLink className="mr-2 h-3.5 w-3.5" />
            {t('common.open')}
          </Link>
        </Button>
        <DeleteProjectButton project={project} />
      </CardFooter>
    </Card>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

// メールアドレス未確認バナー(確認が済むまでプロジェクトを作れない)
function VerifyEmailBanner({ email }: { email: string }) {
  const { t } = useTranslation()
  const { refreshUser } = useAuth()
  const mutation = useMutation({
    mutationFn: resendVerification,
    onSuccess: (res) => {
      if (res.alreadyVerified) {
        toast.success(t('dashboard.verifyBannerAlready'))
        refreshUser().catch(() => undefined)
      } else {
        toast.success(t('dashboard.verifyBannerResent'))
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('dashboard.verifyBannerResendFailed'))
    },
  })

  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
      <span className="flex items-start gap-2">
        <Mail className="h-4 w-4 mt-0.5 shrink-0" />
        {t('dashboard.verifyBanner', { email })}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? t('dashboard.verifyBannerResending') : t('dashboard.verifyBannerResend')}
      </Button>
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: projects, isLoading, isError } = useQuery({
    queryKey: ['projects'],
    queryFn: listProjects,
  })

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-5xl p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('dashboard.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
          </div>
          <CreateProjectDialog onCreated={() => undefined} disabled={user?.email_verified === false} />
        </div>

        {user && !user.email_verified && <VerifyEmailBanner email={user.email} />}

        {/* ドメイン未登録バナー */}
        {user && !user.domain && (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {t('dashboard.domainBanner')}{' '}
              <Link to="/account" className="font-medium underline underline-offset-2 hover:opacity-80">
                {t('dashboard.domainBannerLink')}
              </Link>
            </span>
          </div>
        )}

        {/* 表示名未設定バナー(共有ギャラリーに掲載するには必要) */}
        {user && !user.display_name && (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {t('dashboard.displayNameBanner')}{' '}
              <Link to="/account" className="font-medium underline underline-offset-2 hover:opacity-80">
                {t('dashboard.displayNameBannerLink')}
              </Link>
            </span>
          </div>
        )}

        {/* States */}
        {isLoading && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-36 animate-pulse bg-muted" />
            ))}
          </div>
        )}

        {isError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{t('dashboard.loadFailed')}</p>
          </div>
        )}

        {projects && projects.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 gap-3">
            <p className="text-muted-foreground text-sm">{t('dashboard.noProjects')}</p>
            <CreateProjectDialog onCreated={() => undefined} />
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
